// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../components/customButtons/Button';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { loginAgribusiness, isAuthenticated, requestPasswordReset, loading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Check for lockout on component mount
  useEffect(() => {
    const storedLockout = localStorage.getItem('loginLockout');
    const storedAttempts = localStorage.getItem('failedLoginAttempts');

    if (storedLockout) {
      const lockoutExpiry = parseInt(storedLockout);
      if (Date.now() < lockoutExpiry) {
        setLockoutTime(lockoutExpiry);
      } else {
        localStorage.removeItem('loginLockout');
        localStorage.removeItem('failedLoginAttempts');
      }
    }

    if (storedAttempts) {
      setFailedAttempts(parseInt(storedAttempts));
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Enhanced validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Valid email address required';
    if (email.length > 254) return 'Email too long';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    if (password.length > 128) return 'Password too long';
    return '';
  };

  // Debounced email validation
  useEffect(() => {
    if (formData.email) {
      const timer = setTimeout(() => {
        const emailError = validateEmail(formData.email);
        if (emailError && formData.email) {
          setErrors(prev => ({ ...prev, email: emailError }));
        } else if (errors.email) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
          });
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [formData.email, errors.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Sanitize input
    let sanitizedValue = value;
    if (name === 'email') {
      sanitizedValue = value.toLowerCase().trim();
    }

    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear API error when user starts typing
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    newErrors.email = validateEmail(formData.email);
    newErrors.password = validatePassword(formData.password);

    // Remove empty error messages
    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if account is locked out
  const isAccountLocked = () => {
    if (!lockoutTime) return false;
    return Date.now() < lockoutTime;
  };

  // Handle failed login attempt
  const handleFailedAttempt = () => {
    const newAttempts = failedAttempts + 1;
    setFailedAttempts(newAttempts);
    localStorage.setItem('failedLoginAttempts', newAttempts.toString());

    // Lock account after 5 failed attempts for 15 minutes
    if (newAttempts >= 5) {
      const lockoutDuration = 15 * 60 * 1000; // 15 minutes
      const lockoutExpiry = Date.now() + lockoutDuration;
      setLockoutTime(lockoutExpiry);
      localStorage.setItem('loginLockout', lockoutExpiry.toString());
    }
  };

  // Reset failed attempts on successful login
  const resetFailedAttempts = () => {
    setFailedAttempts(0);
    setLockoutTime(null);
    localStorage.removeItem('failedLoginAttempts');
    localStorage.removeItem('loginLockout');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if account is locked
    if (isAccountLocked()) {
      const remainingTime = Math.ceil((lockoutTime - Date.now()) / 60000); // minutes
      setApiError(`Account temporarily locked. Please try again in ${remainingTime} minutes.`);
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError('');

    try {
      console.log('🚀 Logging in...');

      await loginAgribusiness(formData);

      // Reset failed attempts on success
      resetFailedAttempts();

      // Clear form
      setFormData({ email: '', password: '' });

    } catch (err) {
      console.error('Login failed:', err.message);

      // Handle failed attempt
      handleFailedAttempt();

      // Enhanced error messages
      let errorMessage = err.message || 'An unexpected error occurred during login.';

      if (err.message.includes('network') || err.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message.includes('verify your email')) {
        errorMessage = (
          <span className='font-body'>
            Please verify your email before logging in.{' '}
            <button
              type="button"
              onClick={() => setShowResetPassword(true)}
              className="underline hover:text-primary-light font-medium"
            >
              Resend verification email?
            </button>
          </span>
        );
      } else if (err.message.includes('Invalid credentials') || err.message.includes('Invalid email or password')) {
        const remainingAttempts = 5 - failedAttempts;
        errorMessage = `Invalid email or password. ${remainingAttempts > 0 ? `${remainingAttempts} attempts remaining before temporary lockout.` : 'Account locked for 15 minutes.'}`;
      }

      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle password reset request
  const handlePasswordReset = async () => {
    if (!formData.email) {
      setApiError('Please enter your email address to reset your password.');
      return;
    }

    const emailError = validateEmail(formData.email);
    if (emailError) {
      setApiError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setApiError('');

    try {
      await requestPasswordReset(formData.email);
      setApiError('Password reset instructions have been sent to your email.');
      setShowResetPassword(false);
    } catch (error) {
      setApiError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate remaining lockout time
  const getRemainingLockoutTime = () => {
    if (!lockoutTime) return 0;
    return Math.ceil((lockoutTime - Date.now()) / 60000); // minutes
  };

  if (loading) {
    return (
      <div className="h-screen bg-neutral flex items-center justify-center font-sans text-primary text-xl">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 mx-auto text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 font-body">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="h-screen bg-neutral flex flex-col justify-center px-4 sm:px-6 lg:px-8 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-2xl font-extrabold text-primary mb-4 font-heading">
          Sign In to Your Account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 max-w-sm mx-auto font-body">
          Welcome back! Please enter your credentials to continue.
        </p>
      </div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-10 px-6 shadow rounded-2xl sm:px-12 border border-gray-50 animate-fade-in">

          {/* Security Warning for Lockout */}
          {failedAttempts > 0 && !isAccountLocked() && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <span className="text-yellow-700 font-medium font-body">Security Notice</span>
              </div>
              <p className="text-yellow-600 text-sm mt-1 font-body">
                {failedAttempts} failed attempt(s). Account will be locked after 5 attempts.
              </p>
            </div>
          )}

          {/* Account Locked Warning */}
          {isAccountLocked() && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
                <span className="text-red-700 font-medium font-body">Account Temporarily Locked</span>
              </div>
              <p className="text-red-600 text-sm mt-1 font-body">
                Too many failed attempts. Please try again in {getRemainingLockoutTime()} minutes.
              </p>
            </div>
          )}

          {apiError && (
            <div className="mb-6 text-red-600 text-center font-medium p-3 rounded-lg bg-red-50 border border-red-100 font-body">
              {apiError}
            </div>
          )}

          {/* Password Reset Form */}
          {showResetPassword && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2 font-heading">Reset Password</h3>
              <p className="text-gray-600 text-sm mb-3 font-body">
                We'll send a password reset link to: <strong>{formData.email}</strong>
              </p>
              <div className="flex space-x-2">
                <Button
                  onClick={handlePasswordReset}
                  className="flex-1 py-2 text-sm font-accent"
                  variant="primary"
                >
                  Send Reset Link
                </Button>
                <Button
                  onClick={() => setShowResetPassword(false)}
                  className="flex-1 py-2 text-sm font-accent"
                  variant="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength="254"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className={`block w-full border ${errors.email ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                disabled={isAccountLocked()}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  minLength="6"
                  maxLength="128"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={`block w-full border ${errors.password ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4 pr-10
                             focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                  disabled={isAccountLocked()}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={isAccountLocked()}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.54 18.54 0 0 1 2.58-3.15M2.37 2.37L21.63 21.63M16.13 16.13a4 4 0 0 1-5.42-5.42M4.72 4.72A10.07 10.07 0 0 1 12 4c7 0 11 8 11 8a18.54 18.54 0 0 1-2.58 3.15" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary hover:text-primary-light font-body"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                disabled={isSubmitting || isAccountLocked()}
                className="w-full py-3 px-4 text-base rounded-lg font-accent"
                variant="primary"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing In...
                  </span>
                ) : isAccountLocked() ? 'Account Locked' : 'Sign In'}
              </Button>
            </div>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500 font-medium font-body">
                  Don't have an account?
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/register"
                className="text-primary hover:text-primary-light font-medium text-sm hover:underline font-body"
              >
                Create an account
              </Link>
            </div>
          </div>

          {/* Security Footer */}
          <div className="mt-6 text-xs text-gray-500 text-center font-body">
            <p>Your login is protected with industry-standard security measures.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;